import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { orderApi } from '../../services/api'
import Button from '../../components/common/Button'

/**
 * Fulfillment — list of orders to pack + status tracking.
 *
 * Unified list: orders show ONCE, regardless of whether they also have a
 * backorder slip pending. Cards get a "📦 Backorder ready" badge when a
 * pending batch exists. Clicking "Fill Order" takes the chair into
 * FillOrder.jsx which handles the backorder pack flow if needed.
 *
 * Status filter options:
 *   - unpacked        (pending_payment OR paid — main order needs packing)
 *   - packed          (ready for pickup)
 *   - picked_up       (done)
 *   - backorder_ready (any order with a pending backorder slip batch)
 *   - all (active)
 *
 * Each card shows a 3-step progress indicator (Paid → Packed → Picked Up)
 * rather than a single status badge, so the chair sees the full pipeline.
 */
export default function Fulfillment() {
    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(true)
    const [statusFilter, setStatusFilter] = useState('backorder_ready')

    // Set of order ids with at least one pending backorder slip batch
    const [pendingBackorderOrderIds, setPendingBackorderOrderIds] = useState(new Set())

    useEffect(() => {
        load()
        loadPendingBackorders()
    }, [])

    async function load() {
        setLoading(true)
        try {
            const response = await orderApi.listAll()
            if (response.success) setOrders(response.orders)
            else toast.error(response.message || 'Could not load orders')
        } catch {
            toast.error('Network error loading orders')
        } finally {
            setLoading(false)
        }
    }

    async function loadPendingBackorders() {
        try {
            const response = await orderApi.listPendingBackorderSlips()
            if (response.success) {
                const ids = new Set((response.pending || []).map(p => p.orderId))
                setPendingBackorderOrderIds(ids)
            }
        } catch {
            // non-fatal
        }
    }

    async function handleMarkPickedUp(order) {
        if (!confirm(`Mark order #${order.id} for ${order.customerName} as picked up?`)) return
        try {
            const response = await orderApi.updateStatus(order.id, { status: 'picked_up' })
            if (response.success) {
                toast.success(`Order #${order.id} marked as picked up`)
                load()
            } else {
                toast.error(response.message || 'Update failed')
            }
        } catch {
            toast.error('Network error')
        }
    }

    const filtered = orders.filter(o => {
        if (statusFilter === 'unpacked') return o.status === 'paid' || o.status === 'pending_payment'
        if (statusFilter === 'all') return o.status !== 'cancelled'
        if (statusFilter === 'backorder_ready') return pendingBackorderOrderIds.has(o.id)
        return o.status === statusFilter
    })

    return (
        <div className="space-y-4">
            <div>
                <h2 className="text-2xl font-semibold text-ghasc-navy">Fulfillment</h2>
                <p className="text-sm text-gray-500 mt-1">
                    Pack orders, mark pickups, deliver backorders. Orders flagged <strong>Pending Payment</strong> still
                    need cash collected at pickup — record the payment afterward on the Pending Payments page.
                </p>
            </div>

            {/* Filter bar */}
            <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap gap-3 items-end">
                <div className="min-w-[260px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    >
                        <option value="backorder_ready">📦 Backorder Ready ({pendingBackorderOrderIds.size})</option>
                        <option value="unpacked">Unpacked — ready to pack</option>
                        <option value="packed">Packed — ready for pickup</option>
                        <option value="picked_up">Picked Up</option>
                        <option value="all">All (active)</option>
                    </select>
                </div>
                <div className="flex-1 text-right text-sm">
                    <div className="text-xs uppercase tracking-wider text-gray-500">Count</div>
                    <div className="text-2xl font-semibold text-ghasc-navy">{filtered.length}</div>
                </div>
            </div>

            {/* Cards */}
            {loading ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">Loading...</div>
            ) : filtered.length === 0 ? (
                <div className="bg-white rounded-xl shadow-sm p-8 text-center text-gray-500">
                    {statusFilter === 'unpacked'
                        ? 'Nothing to pack right now. 🌴'
                        : statusFilter === 'backorder_ready'
                            ? 'No backorders ready to deliver right now.'
                            : 'No orders match that filter.'}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {filtered.map(o => {
                        const isPacked = o.status === 'packed'
                        const isPickedUp = o.status === 'picked_up'
                        const isPendingPayment = o.status === 'pending_payment'
                        const hasBackorderReady = pendingBackorderOrderIds.has(o.id)
                        return (
                            <div key={o.id} className={`bg-white rounded-xl shadow-sm p-4 flex flex-col gap-3 ${isPickedUp && !hasBackorderReady ? 'opacity-75' : ''}`}>
                                {/* Top row */}
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <div className="text-ghasc-navy font-semibold">Order #{o.id}</div>
                                        <div className="text-xs text-gray-500 mt-0.5">
                                            {new Date(o.createdAt).toLocaleDateString('en-US', {
                                                month: 'short', day: 'numeric', year: 'numeric',
                                            })}
                                        </div>
                                    </div>
                                    {hasBackorderReady && (
                                        <span className="flex-shrink-0 inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                            📦 Backorder ready
                                        </span>
                                    )}
                                </div>

                                {/* Status progress: ✓ Paid → ✓ Packed → ✓ Picked Up */}
                                <StatusProgress order={o} />

                                {/* Customer + submitter */}
                                <div>
                                    <div className="text-sm font-medium text-gray-900 break-words">{o.customerName}</div>
                                    <div className="text-xs text-gray-500">Submitted by {o.submitterName}</div>
                                </div>

                                {/* Pending payment warning — cash to collect at pickup */}
                                {isPendingPayment && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900">
                                        💵 Cash to be collected at pickup
                                    </div>
                                )}

                                {/* Item count + total */}
                                <div className="grid grid-cols-2 gap-2 text-sm">
                                    <div>
                                        <div className="text-xs uppercase tracking-wider text-gray-500">Items</div>
                                        <div className="font-medium">
                                            {o.lineCount}
                                            {o.backorderCount > 0 && (
                                                <span className="ml-1 text-xs text-amber-700">
                                                    ({o.backorderCount} BO)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs uppercase tracking-wider text-gray-500">Total</div>
                                        <div className="font-semibold text-ghasc-navy">${Number(o.subtotal).toFixed(2)}</div>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="pt-2 border-t border-gray-100 space-y-2">
                                    {/* If backorder ready, link goes to FillOrder which handles the backorder pack flow */}
                                    {hasBackorderReady ? (
                                        <Link to={`/admin/fulfillment/${o.id}`} className="block">
                                            <Button className="w-full">Deliver Backorder</Button>
                                        </Link>
                                    ) : !isPacked && !isPickedUp ? (
                                        <Link to={`/admin/fulfillment/${o.id}`} className="block">
                                            <Button className="w-full">Fill Order</Button>
                                        </Link>
                                    ) : isPacked ? (
                                        <>
                                            <Link to={`/admin/fulfillment/${o.id}`} className="block">
                                                <Button variant="secondary" className="w-full">View Order</Button>
                                            </Link>
                                            <Button className="w-full" onClick={() => handleMarkPickedUp(o)}>
                                                Mark Picked Up
                                            </Button>
                                        </>
                                    ) : (
                                        <Link to={`/admin/fulfillment/${o.id}`} className="block">
                                            <Button variant="secondary" className="w-full">View Order</Button>
                                        </Link>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

// ============================================================================
//  StatusProgress — 3-step progress indicator
//  ✓ Paid    ✓ Packed    ○ Picked Up
//  Filled checkmark = done. Empty circle = not yet.
//  Cancelled orders show a different state.
// ============================================================================
function StatusProgress({ order }) {
    const isCancelled = order.status === 'cancelled'

    if (isCancelled) {
        return (
            <div className="text-xs text-gray-600 font-medium">
                <span className="inline-block px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                    Cancelled
                </span>
            </div>
        )
    }

    // Determine which steps are done based on the order's timestamps + status
    const isPaid = !!order.paymentMethod
    const isPacked = order.status === 'packed' || order.status === 'picked_up'
    const isPickedUp = order.status === 'picked_up'

    return (
        <div className="flex items-center justify-between text-xs">
            <Step label="Paid" done={isPaid} />
            <Connector done={isPacked} />
            <Step label="Packed" done={isPacked} />
            <Connector done={isPickedUp} />
            <Step label="Picked Up" done={isPickedUp} />
        </div>
    )
}

function Step({ label, done }) {
    return (
        <div className="flex items-center gap-1">
            <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${done
                ? 'bg-green-500 text-white'
                : 'bg-gray-200 text-gray-400 border border-gray-300'
                }`}>
                {done ? '✓' : '○'}
            </span>
            <span className={done ? 'text-gray-900 font-medium' : 'text-gray-400'}>
                {label}
            </span>
        </div>
    )
}

function Connector({ done }) {
    return (
        <div className={`flex-1 h-0.5 mx-1 ${done ? 'bg-green-400' : 'bg-gray-200'}`} />
    )
}